import { Box, Button, CircularProgress } from "@mui/material";
import { Theme } from "@mui/system";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { BffOe, OeKpis } from "aint-types";
import { useEffect, useState } from "react";

import {
  EditOeKpisDialogEventPayload,
  EditOeKpisDialogEventTypes,
} from "../edit-oe-kpis-dialog/edit-oe-kpis-dialog";
import {
  extractKpisFromRows,
  generateColumns,
  generateRows,
  isCellEditable,
} from "./oe-kpis-data-grid-utils";
import { OeKpisDataGridWrapper } from "./oe-kpis-data-grid-wrapper";

const START_YEAR = 2023;
const END_YEAR = 2030;

export type OeKpisDataGridRow = {
  id: number;
  kpi: string;
  field: keyof OeKpis;
};

export type OeKpisDataGridProps = {
  editable: boolean;
  operatingEntity?: BffOe;
  fireEvent?:
    | ((
        event: EditOeKpisDialogEventTypes,
        payload?: EditOeKpisDialogEventPayload
      ) => void)
    | undefined;
};

export default function OeKpisDataGrid({
  fireEvent,
  editable,
  operatingEntity,
}: OeKpisDataGridProps) {
  const apiRef = useGridApiRef();

  const [columns, setColumns] = useState<ReturnType<typeof generateColumns>>(
    []
  );
  const { rows: newRows, updatedOe } = generateRows({
    startYear: START_YEAR,
    endYear: END_YEAR,
    editable,
    operatingEntity,
  });
  const [stateOe, setStateOe] = useState<BffOe | undefined>(updatedOe);
  const [rows, setRows] = useState<OeKpisDataGridRow[]>(newRows);
  useEffect(() => {
    setColumns(
      generateColumns({ startYear: START_YEAR, endYear: END_YEAR, editable })
    );
    const { rows: newRows, updatedOe } = generateRows({
      startYear: START_YEAR,
      endYear: END_YEAR,
      operatingEntity: operatingEntity,
      editable,
    });
    setRows(newRows);
    setStateOe(updatedOe);
  }, [editable, operatingEntity]);
  useEffect(() => setStateOe(operatingEntity), [operatingEntity]);

  if (
    !operatingEntity ||
    !stateOe ||
    rows.length === 0 ||
    columns.length === 0
  ) {
    return (
      <Box
        sx={{
          display: "flex",
          height: "600px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress
          size={50}
          sx={{ color: (theme: Theme) => theme.palette.action }}
        />
      </Box>
    );
  }

  return (
    <OeKpisDataGridWrapper
      sx={{
        ".MuiDataGrid-root": {
          borderRadius: editable ? 0 : "12px",
        },
        ".MuiDataGrid-columnHeader[data-field='2024-Q4-plan']": {
          ".MuiDataGrid-columnHeaderDraggableContainer": {
            flexDirection: "column",
            alignItems: "center",
          },
        },
      }}
    >
      <DataGrid
        className={editable ? "MuiDataGrid-root--editable" : "MuiDataGrid-root"}
        rows={rows}
        apiRef={apiRef}
        columns={columns}
        hideFooter={!editable}
        disableColumnMenu
        disableColumnSorting
        disableColumnFilter
        disableColumnResize
        // Make line breaks for kpi names possible
        getRowHeight={() => "auto"}
        // This is needed to prevent the grid from removing offscreen cells.
        // This screws with the odd/even styling and makes the column coloring flicker
        disableVirtualization
        slots={{
          footer: () => (
            <CustomFooter
              onSaveClick={() => {
                fireEvent?.("popup:save", extractKpisFromRows(rows, false));
              }}
              onCancelClick={() => {
                fireEvent?.("popup:close");
              }}
            />
          ),
        }}
        isCellEditable={(params) => {
          const field = params.row.field;
          const [, quarterString, planOrActual] = params.field.split("-");
          const quarter = +quarterString.charAt(1);

          return isCellEditable({ field, quarter, planOrActual });
        }}
        processRowUpdate={(updatedRow, oldRow) => {
          // Get changed field name
          const changedFieldName = Object.keys(updatedRow).find(
            (key) =>
              updatedRow[key as keyof OeKpisDataGridRow] !==
              oldRow[key as keyof OeKpisDataGridRow]
          ) as keyof OeKpisDataGridRow;

          if (!changedFieldName) {
            return updatedRow;
          }

          // Round value to two decimals
          const newValue = (
            Math.round(
              (Number(updatedRow[changedFieldName]) + Number.EPSILON) * 100
            ) / 100
          ).toString();

          // Update state oe to reflect changes
          const [year, quarterString, planOrActual] =
            changedFieldName.split("-");
          const quarter = +quarterString.charAt(1);

          const kpi = stateOe[updatedRow.field];
          const existingIndex = kpi.findIndex(
            (kpiObj) => kpiObj.year === year && kpiObj.quarter === quarter
          );
          let newStateOe;
          if (existingIndex === -1) {
            newStateOe = {
              ...stateOe,
              [updatedRow.field]: [
                ...kpi,
                {
                  quarter,
                  year,
                  ...(planOrActual === "plan"
                    ? { planValue: newValue }
                    : { actualValue: newValue }),
                },
              ],
            };
          } else {
            newStateOe = {
              ...stateOe,
              [updatedRow.field]: kpi.map((kpiObj) =>
                kpiObj.year === year && kpiObj.quarter === quarter
                  ? {
                      ...kpiObj,
                      ...(planOrActual === "plan"
                        ? { planValue: newValue }
                        : { actualValue: newValue }),
                    }
                  : kpiObj
              ),
            };
          }

          const { rows: newRows, updatedOe } = generateRows({
            startYear: START_YEAR,
            endYear: END_YEAR,
            operatingEntity: newStateOe,
            editable,
          });

          setStateOe(updatedOe);
          setRows(newRows);

          return newRows.find((row) => row.id === updatedRow.id) ?? updatedRow;
        }}
        onProcessRowUpdateError={(error) => console.log(error)}
      />
    </OeKpisDataGridWrapper>
  );
}

const CustomFooter = ({
  onSaveClick,
  onCancelClick,
}: {
  onSaveClick: () => void;
  onCancelClick: () => void;
}) => {
  return (
    <Box
      sx={{
        gap: "8px",
        marginTop: "8px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <span>
        *Figures for quarters Q1 to Q3 are automatically calculated from Q4
        figures
      </span>
      <span>** N.P. = Not Provided</span>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <Button
          sx={{ borderRadius: "4px" }}
          variant="outlined"
          onClick={onCancelClick}
        >
          Cancel
        </Button>
        <Button
          sx={{ color: "white", borderRadius: "4px" }}
          variant="contained"
          onClick={onSaveClick}
        >
          save
        </Button>
      </Box>
    </Box>
  );
};
